import { AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@rudi/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { Badge } from '@rudi/ui/badge';
import { QueryState } from '../components/page';
import { useDeleteUser, useUserDeletionImpact } from '../api/hooks';
import type { AdminUser, UserDeletionContentImpact } from '../api/types';

function totalPersonal(content: UserDeletionContentImpact): number {
  return content.personalDocuments + content.personalLists + content.personalDashboards;
}

function totalWorkspace(content: UserDeletionContentImpact): number {
  return (
    content.workspaceDocuments +
    content.workspaceLists +
    content.workspaceDashboards +
    content.workspaceRows +
    content.workspaceColumns +
    content.workspaceAttachments
  );
}

function errorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: string; error?: string } } })?.response
    ?.data;
  return detail?.detail ?? detail?.error ?? (error instanceof Error ? error.message : fallback);
}

export function DeleteUserDialog({
  user,
  open,
  onClose,
  onDeleted,
}: {
  user: AdminUser | null;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const impact = useUserDeletionImpact(user?.id ?? null, open);
  const del = useDeleteUser();
  const content = impact.data?.content;
  const blocked = impact.data?.blocked ?? true;

  const onDelete = async () => {
    if (!user || blocked || del.isPending || impact.isLoading || impact.error) return;
    try {
      await del.mutateAsync(user.id);
      toast.success('User deleted');
      onDeleted?.();
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, 'Delete failed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => !next && !del.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            Delete user
          </DialogTitle>
          <DialogDescription>
            {user
              ? `${user.display_name ?? user.username} will lose access to Rudi. Their personal content will be permanently deleted. This cannot be undone.`
              : 'User deletion impact will be checked before deletion.'}
          </DialogDescription>
        </DialogHeader>

        <QueryState isLoading={impact.isLoading} error={impact.error}>
          {impact.data && content && (
            <div className="space-y-4">
              <div className="rounded-3xl border p-3 text-sm">
                <div className="font-medium">{impact.data.displayName ?? impact.data.username}</div>
                <div className="text-muted-foreground">{impact.data.email ?? 'No email'}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {impact.data.tenantName && (
                    <Badge variant="outline">{impact.data.tenantName}</Badge>
                  )}
                  {impact.data.roleNames.map(role => (
                    <Badge key={role} variant="secondary">
                      {role}
                    </Badge>
                  ))}
                </div>
              </div>

              {impact.data.blockers.length > 0 && (
                <div className="rounded-3xl border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                  <div className="mb-2 flex items-center gap-2 font-medium">
                    <AlertTriangle className="h-4 w-4" />
                    Deletion blocked
                  </div>
                  <ul className="list-disc space-y-1 pl-5">
                    {impact.data.blockers.map(blocker => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <div className="rounded-3xl border p-3">
                  <div className="font-medium">Deleted personal content</div>
                  <div className="mt-1 text-2xl font-semibold">{totalPersonal(content)}</div>
                  <div className="mt-2 text-muted-foreground">
                    {content.personalDocuments} docs, {content.personalLists} lists,{' '}
                    {content.personalDashboards} dashboards
                  </div>
                </div>
                <div className="rounded-3xl border p-3">
                  <div className="font-medium">Preserved workspace content</div>
                  <div className="mt-1 text-2xl font-semibold">{totalWorkspace(content)}</div>
                  <div className="mt-2 text-muted-foreground">
                    {content.workspaceDocuments} docs, {content.workspaceLists} lists,{' '}
                    {content.workspaceDashboards} dashboards
                  </div>
                </div>
              </div>
            </div>
          )}
        </QueryState>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={del.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onDelete}
            disabled={blocked || del.isPending || impact.isLoading || !!impact.error}
          >
            {del.isPending ? 'Deleting…' : 'Delete user'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
